import {
  Box,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material';
import { IOrder, IPrescription, Statuses } from 'API';
import { CustomStepperIcon, ICustomStepperIcon } from 'Components';
import { format } from 'date-fns/esm';
import { useIsDesktop } from 'Hooks';

interface PrescriptionStepperProps {
  item: IPrescription | IOrder;
}

const CustomStepMobile = styled(StepConnector)(() => ({
  marginLeft: '11.5px',
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#1752AF',
      minHeight: '40px',
    },
  },
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      minHeight: '40px',
    },
  },
}));

const CustomStepDesktop = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#C6D3DB',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#1752AF',
    },
  },
}));

const CustomConnector = () => {
  const isDesktop = useIsDesktop();
  return isDesktop ? <CustomStepDesktop /> : <CustomStepMobile />;
};

const suppliedStatusIds = [6, 7];

export const PrescriptionStepper = (props: PrescriptionStepperProps) => {
  const isDesktop = useIsDesktop();
  const { item } = props;

  const isStepCompleted = (statusIndex: number) => {
    const currentStatusIndex = item.statuses.findIndex(
      (s) => s.calcStatus === item.currentStatus
    );
    return currentStatusIndex !== -1 && statusIndex <= currentStatusIndex;
  };

  const isCurrentStep = (statusIndex: number) => {
    return item.statuses[statusIndex].calcStatus === item.currentStatus;
  };
  const formatStatusDate = (status: Statuses) => {
    if (status.statusDate) {
      return format(new Date(status.statusDate), 'dd/MM/yyyy');
    }
    return '';
  };

  const getCustomStepperIcon = (props: ICustomStepperIcon) => {
    const newProps = suppliedStatusIds.includes(item.currentStatus)
      ? {
          ...props,
          active: false,
          completed: true,
          isLastStep: false,
        }
      : props;
    return CustomStepperIcon(newProps);
  };

  return isDesktop ? (
    <Stepper alternativeLabel connector={<CustomConnector />}>
      {item.statuses.map((status, statusIndex) => (
        <Step
          key={status.calcStatus}
          completed={isStepCompleted(statusIndex)}
          sx={{ flex: '1 1 0', width: '0' }}
          active={isCurrentStep(statusIndex)}
        >
          <StepLabel
            StepIconComponent={getCustomStepperIcon}
            sx={{
              '& .MuiStepLabel-alternativeLabel': {
                mt: '2px',
              },
            }}
          >
            <Typography component='h3' fontSize={18}>
              {status.label}
            </Typography>
            <Typography>{formatStatusDate(status)}</Typography>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  ) : (
    <Stepper
      orientation='vertical'
      connector={<CustomConnector />}
      sx={{ marginTop: '16px', marginBottom: '48px' }}
    >
      {item.statuses.map((status, statusIndex) => (
        <Step
          key={status.calcStatus}
          completed={Boolean(status.statusDate)}
          active={isCurrentStep(statusIndex)}
          sx={{ width: '100%' }}
        >
          <StepLabel
            StepIconComponent={getCustomStepperIcon}
            sx={{
              position: 'relative',
              p: 0,
              top: isCurrentStep(statusIndex) ? '3px' : '',
              marginTop: isCurrentStep(statusIndex) ? '-7px' : '',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '-1.5px',
              }}
            >
              <Typography
                color={isCurrentStep(statusIndex) ? '#1752AF' : ''}
                fontWeight={isCurrentStep(statusIndex) ? 700 : ''}
              >
                {status.label}
              </Typography>
              <Typography
                fontSize={'14px'}
                color={isCurrentStep(statusIndex) ? '#1752AF' : '#60727E'}
              >
                {formatStatusDate}
              </Typography>
            </Box>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};
